/**
 * auditScoring.js
 *
 * Centralised scoring, weighting, and mandatory-fail logic for Audit / Inspection forms.
 *
 * ─── FORM-STRUCTURE SCHEMA (added fields) ────────────────────────────────────
 *
 *  formStructure.scoring = {
 *    enabled: true,
 *    thresholds: {
 *      fail:             60,   // below this % → FAIL  (status 15)
 *      needsImprovement: 80,   // below this % → NEEDS IMPROVEMENT (status 14)
 *    }
 *  }
 *
 * ─── PER-MODULE SCHEMA (added field) ─────────────────────────────────────────
 *
 *  module.weight = 10          // integer, how many points this question is worth
 *                              // omit (or null) to exclude from scoring
 *
 * ─── CRITICALITY → SCORING MAPPING ──────────────────────────────────────────
 *
 *  High   → 0 pts + triggers mandatory fail
 *  Medium → 0 pts (no mandatory fail)
 *  Low    → FULL points  ← "Safe" / "N/A" style answers live here
 *  (none) → FULL points
 *
 * ─── STATUS CODES (unchanged from existing system) ───────────────────────────
 *   15 = FAIL
 *   14 = NEEDS IMPROVEMENT
 *   13 = PASS
 *    8 = COMPLETED (no thresholds / scoring)
 *
 * ─── PRECEDENCE (highest priority first) ─────────────────────────────────────
 *   1. Mandatory-fail  (any High-crit answer selected)          → always 15
 *   2. Threshold check (existing count-based logic)             → 15 or 14
 *   3. Scoring %       (weighted % against configured bands)    → 15, 14, or pass
 *   4. Pass            (none of the above triggered)            → 13
 */

// ─── shared helpers ───────────────────────────────────────────────────────────

const normalizeAuditCrit = (ac) => ({
  High:   Array.isArray(ac?.High)   ? ac.High   : [],
  Medium: Array.isArray(ac?.Medium) ? ac.Medium : [],
  Low:    Array.isArray(ac?.Low)    ? ac.Low    : [],
});

const CRIT_LEVELS = ["High", "Medium", "Low"];

// ─── per-module helpers ───────────────────────────────────────────────────────

/**
 * Get the criticality level of a specific option within a module.
 * Returns "High" | "Medium" | "Low" | null
 */
export const getOptionCritLevel = (module, option) => {
  const { High, Medium, Low } = normalizeAuditCrit(module?.auditCriticality);
  if (High.includes(option))   return "High";
  if (Medium.includes(option)) return "Medium";
  if (Low.includes(option))    return "Low";
  return null;
};

/**
 * Returns true if the given selected option(s) include any High-criticality answer
 * for this module — i.e. a mandatory-fail trigger.
 */
export const isMandatoryFail = (module, selected) => {
  const { High } = normalizeAuditCrit(module?.auditCriticality);
  if (!High.length) return false;
  const arr = Array.isArray(selected) ? selected : selected ? [selected] : [];
  return arr.some((opt) => High.includes(opt));
};

// ─── scoring engine ───────────────────────────────────────────────────────────

/**
 * Walk all modules and compute a weighted score.
 *
 * Scoring rules per answer:
 *   High-tagged option selected   → 0 pts, sets mandatoryFail = true
 *   Medium-tagged option selected → 0 pts
 *   Low-tagged option selected    → FULL weight (these are the "Safe"/"N/A" answers)
 *   Untagged option selected      → FULL weight
 *   Unanswered weighted question  → excluded from both earned and possible totals
 *
 * @param {object[]} modules   - formStructure.modules array
 * @param {object}   responses - current response map  { [moduleId]: value }
 *
 * @returns {{
 *   earned:       number,
 *   possible:     number,
 *   percent:      number | null,
 *   mandatoryFail: boolean,
 *   weightedModuleIds: string[]
 * }}
 */
export const computeScoringResult = (modules, responses) => {
  let earned         = 0;
  let possible       = 0;
  let mandatoryFail  = false;
  const weightedIds  = [];

  const scoreModule = (mod) => {
    const weight = typeof mod.weight === "number" && mod.weight > 0
      ? mod.weight
      : null;

    if (weight === null) return; // not participating in scoring

    const ac = normalizeAuditCrit(mod.auditCriticality);

    if (mod.type === "audit_checkbox_group") {
      const raw      = responses?.[mod.id];
      const selected = Array.isArray(raw) ? raw : raw ? [raw] : [];
      if (!selected.length) return; // unanswered — skip

      weightedIds.push(mod.id);
      possible += weight;

      const hitHigh   = ac.High.some((opt) => selected.includes(opt));
      // FIX: only High and Medium are failing answers; Low = passing
      const hitFail   = hitHigh || ac.Medium.some((opt) => selected.includes(opt));

      if (hitHigh) mandatoryFail = true;
      if (!hitFail) earned += weight;
    }

    if (mod.type === "inspection_item") {
      const raw = responses?.[mod.id];
      const sel = raw?.selection ?? (typeof raw === "string" ? raw : null);
      if (!sel) return; // unanswered — skip

      weightedIds.push(mod.id);
      possible += weight;

      const hitHigh = ac.High.includes(sel);
      // FIX: only High and Medium are failing answers; Low = passing
      const hitFail = hitHigh || ac.Medium.includes(sel);

      if (hitHigh) mandatoryFail = true;
      if (!hitFail) earned += weight;
    }
  };

  const walk = (mod) => {
    scoreModule(mod);
    if (Array.isArray(mod.children)) mod.children.forEach(walk);
    if (mod.type === "dynamic_question_group" && Array.isArray(mod.questions)) {
      mod.questions.forEach(scoreModule);
    }
  };

  (modules || []).forEach(walk);

  const percent = possible > 0 ? Math.round((earned / possible) * 100) : null;

  return { earned, possible, percent, mandatoryFail, weightedModuleIds: weightedIds };
};

// ─── combined evaluator ───────────────────────────────────────────────────────

/**
 * Evaluate the final audit outcome using ALL active mechanisms.
 *
 * @param {object}   formStructure
 * @param {object}   responses
 *
 * @returns {{
 *   status:        15 | 14 | 13 | 8 | null,
 *   reason:        "mandatory_fail" | "threshold" | "scoring" | "pass" | "no_thresholds" | null,
 *   percent:       number | null,
 *   mandatoryFail: boolean,
 *   scoringEnabled: boolean,
 *   thresholdEnabled: boolean,
 * }}
 */
export const evaluateCombinedAuditStatus = (formStructure, responses = {}) => {
  if (!formStructure || formStructure.formType !== "Audit") {
    return { status: null, reason: null, percent: null, mandatoryFail: false, scoringEnabled: false, thresholdEnabled: false };
  }

  const scoringConfig   = formStructure.scoring;
  const thresholdConfig = formStructure.threshold;

  const scoringEnabled   = Boolean(scoringConfig?.enabled);
  const thresholdEnabled = Boolean(thresholdConfig?.enabled);

  // ── 1. Scoring calculation (always run if enabled, for percent display)
  const scoringResult = scoringEnabled
    ? computeScoringResult(formStructure.modules || [], responses)
    : { percent: null, mandatoryFail: false };

  // Detect mandatory fail even when scoring is disabled
  const mandatoryFailFromFields = scoringEnabled
    ? scoringResult.mandatoryFail
    : detectMandatoryFail(formStructure.modules || [], responses);

  const percent = scoringResult.percent;

  // ── 2. Mandatory-fail (highest precedence)
  if (mandatoryFailFromFields) {
    return { status: 15, reason: "mandatory_fail", percent, mandatoryFail: true, scoringEnabled, thresholdEnabled };
  }

  // ── 3. Threshold check (existing count-based logic)
  let thresholdStatus = null;
  if (thresholdEnabled) {
    const counts = countCritQuestionHits(formStructure.modules || [], responses);
    const th = thresholdConfig;
    if (meetsBucket(counts, th.fail))                  thresholdStatus = 15;
    else if (meetsBucket(counts, th.needsImprovement)) thresholdStatus = 14;
  }

  // ── 4. Scoring % check
  let scoringStatus = null;
  if (scoringEnabled && percent !== null) {
    const t = scoringConfig.thresholds || {};
    const failPct  = Number(t.fail)             || 0;
    const needsPct = Number(t.needsImprovement) || 0;
    if (percent < failPct)       scoringStatus = 15;
    else if (percent < needsPct) scoringStatus = 14;
  }

  // ── 5. Worst outcome of threshold + scoring
  const worstStatus = Math.max(thresholdStatus ?? 0, scoringStatus ?? 0);

  if (!thresholdEnabled && !scoringEnabled) {
    return { status: 8, reason: "no_thresholds", percent, mandatoryFail: false, scoringEnabled, thresholdEnabled };
  }

  if (worstStatus === 15) return { status: 15, reason: thresholdStatus === 15 ? "threshold" : "scoring", percent, mandatoryFail: false, scoringEnabled, thresholdEnabled };
  if (worstStatus === 14) return { status: 14, reason: thresholdStatus === 14 ? "threshold" : "scoring", percent, mandatoryFail: false, scoringEnabled, thresholdEnabled };

  return { status: 13, reason: "pass", percent, mandatoryFail: false, scoringEnabled, thresholdEnabled };
};

// ─── internal helpers ─────────────────────────────────────────────────────────

/** Detect mandatory fail without running full scoring (for when scoring is off) */
const detectMandatoryFail = (modules, responses) => {
  let found = false;

  const check = (mod) => {
    if (found) return;
    const ac = normalizeAuditCrit(mod.auditCriticality);
    if (!ac.High.length) return;

    if (mod.type === "audit_checkbox_group") {
      const raw      = responses?.[mod.id];
      const selected = Array.isArray(raw) ? raw : raw ? [raw] : [];
      if (ac.High.some((opt) => selected.includes(opt))) found = true;
    }
    if (mod.type === "inspection_item") {
      const raw = responses?.[mod.id];
      const sel = raw?.selection ?? (typeof raw === "string" ? raw : null);
      if (sel && ac.High.includes(sel)) found = true;
    }
  };

  const walk = (mod) => {
    check(mod);
    if (Array.isArray(mod.children)) mod.children.forEach(walk);
    if (mod.type === "dynamic_question_group" && Array.isArray(mod.questions)) {
      mod.questions.forEach(check);
    }
  };

  (modules || []).forEach(walk);
  return found;
};

/** Count how many questions hit each crit level (existing threshold logic) */
const countCritQuestionHits = (modules, responses) => {
  const counts = { High: 0, Medium: 0, Low: 0 };

  const tally = (mod) => {
    if (mod.type === "audit_checkbox_group") {
      const ac       = normalizeAuditCrit(mod.auditCriticality);
      const raw      = responses?.[mod.id];
      const selected = Array.isArray(raw) ? raw : raw ? [raw] : [];
      CRIT_LEVELS.forEach((lvl) => {
        if (ac[lvl].length && ac[lvl].some((opt) => selected.includes(opt))) counts[lvl] += 1;
      });
    }
    if (mod.type === "inspection_item") {
      const ac  = normalizeAuditCrit(mod.auditCriticality);
      const raw = responses?.[mod.id];
      const sel = raw?.selection ?? (typeof raw === "string" ? raw : null);
      const selected = sel ? [sel] : [];
      CRIT_LEVELS.forEach((lvl) => {
        if (ac[lvl].length && ac[lvl].some((opt) => selected.includes(opt))) counts[lvl] += 1;
      });
    }
    if (Array.isArray(mod.children)) mod.children.forEach(tally);
    if (mod.type === "dynamic_question_group" && Array.isArray(mod.questions)) {
      mod.questions.forEach(tally);
    }
  };

  (modules || []).forEach(tally);
  return counts;
};

const meetsBucket = (counts, bucket) => {
  if (!bucket) return false;
  const needH = Number(bucket.High)   || 0;
  const needM = Number(bucket.Medium) || 0;
  const needL = Number(bucket.Low)    || 0;
  return (
    (needH > 0 && counts.High   >= needH) ||
    (needM > 0 && counts.Medium >= needM) ||
    (needL > 0 && counts.Low    >= needL)
  );
};

// ─── banner helper ────────────────────────────────────────────────────────────

/**
 * Map a combined evaluation result to the banner display object.
 */
export const buildAuditBanner = (evalResult) => {
  if (!evalResult) return null;
  const { status, reason, percent, scoringEnabled, thresholdEnabled } = evalResult;

  if (!scoringEnabled && !thresholdEnabled) return null;

  const percentLabel = scoringEnabled && percent !== null ? `${percent}%` : null;

  const make = (color, text) => ({
    color,
    text,
    percentLabel,
    showCounts: reason === "threshold" || reason === "mandatory_fail",
  });

  if (status === 15) {
    if (reason === "mandatory_fail") {
      return { ...make("#db441f", "FAIL"), mandatoryFailBanner: true };
    }
    return make("#db441f", "FAIL");
  }
  if (status === 14) return make("#f59c16", "NEEDS IMPROVEMENT");
  if (status === 13) return make("#80cc5a", "PASS");
  if (status === 8)  return make("#666",    "COMPLETED");

  return null;
};